import { JsonLdType, PersonSchema, BookSchema, ProfilePageSchema } from '../../types/schema-org';
import { speaker } from '../../data/speaker';
import { siteUrl } from './site-url';

/**
 * Identificador estável da pessoa neste domínio. A Geniaria publica a mesma pessoa
 * como `https://geniaria.com.br/#johnny-vaz`; os dois nós se ligam por `sameAs`
 * (aqui → geniaria.com.br/sobre; lá → johnnyvaz.com.br).
 */
export const PERSON_ID = `${siteUrl()}#johnny-vaz`;
const BOOK_ID = `${siteUrl()}#livro-ppa`;

/**
 * Person derivado de src/data/speaker.ts, que segue a bio oficial
 * (PLATAFORMA-CURSOS/docs/JOHNNYVAZ/BIO.md). Cargo, temas, vínculos e formação
 * espelham o Person da Geniaria; nada aqui vai além da bio.
 */
function person(description: string): PersonSchema {
  return {
    '@context': 'https://schema.org',
    '@type': 'Person',
    '@id': PERSON_ID,
    name: speaker.name,
    jobTitle: speaker.schemaJobTitle,
    description,
    url: siteUrl(),
    image: siteUrl(speaker.pressKit.photos[0].original),
    // Perfis oficiais da BIO.md (LinkedIn, YouTube, Instagram) + a mesma pessoa
    // na Geniaria — a lista é igual nos quatro domínios (H-013).
    sameAs: [speaker.social.linkedin, speaker.social.youtube, speaker.social.instagram, speaker.org.profile],
    knowsAbout: [...speaker.knowsAbout],
    worksFor: [
      { '@type': 'Organization', '@id': speaker.org.id, name: speaker.org.name, url: speaker.org.url },
      { '@type': 'Organization', name: speaker.employer.name },
    ],
    hasCredential: speaker.education.map(({ degree, school }) => ({
      '@type': 'EducationalOccupationalCredential' as const,
      credentialCategory: 'degree',
      name: degree,
      recognizedBy: { '@type': 'EducationalOrganization' as const, name: school },
    })),
    alumniOf: speaker.education.map(({ school }) => ({
      '@type': 'EducationalOrganization' as const,
      name: school,
    })),
    award: speaker.credentials.map((c) => `${c.title} (${c.year})`),
  };
}

/** Sem ISBN nem URL: a bio não os dá. */
function book(): BookSchema {
  return {
    '@context': 'https://schema.org',
    '@type': 'Book',
    '@id': BOOK_ID,
    name: speaker.book.title,
    about: speaker.book.about,
    inLanguage: 'pt-BR',
    author: { '@id': PERSON_ID },
  };
}

function profilePage(path: string, name: string): ProfilePageSchema {
  return {
    '@context': 'https://schema.org',
    '@type': 'ProfilePage',
    '@id': `${siteUrl(path)}#profilepage`,
    url: siteUrl(path),
    name,
    inLanguage: 'pt-BR',
    mainEntity: { '@id': PERSON_ID },
  };
}

/** Tira o @context para o nó entrar num @graph. */
function node<T extends { '@context': string }>(schema: T): Omit<T, '@context'> {
  const { '@context': _context, ...rest } = schema;
  return rest;
}

export function generateMetadata(route: string, lang: string): JsonLdType {

  if (route === '/') {
    return { '@context': 'https://schema.org', '@graph': [node(person(speaker.bios.short)), node(book())] };
  }

  if (route === '/builder') {
    return {
      '@context': 'https://schema.org',
      '@type': 'Course',
      name: 'Builder Tech-01: Como Criar com IA sem Ser Dev',
      description: 'Curso para founders, PMs e profissionais não-técnicos que constroem com IA. Aborda segurança, dados, confiabilidade e a dimensão humana do builder — o que a IA não te ensina.',
      url: siteUrl('/builder'),
      inLanguage: 'pt-BR',
      provider: {
        '@type': 'Person',
        name: 'Johnny Vaz',
        url: siteUrl(),
      },
      offers: {
        '@type': 'Offer',
        price: '297',
        priceCurrency: 'BRL',
        availability: 'https://schema.org/InStock',
      },
      hasCourseInstance: {
        '@type': 'CourseInstance',
        courseMode: 'online',
        instructor: {
          '@type': 'Person',
          name: 'Johnny Vaz',
        },
      },
    };
  }

  if (route === '/mentoria') {
    return {
      '@context': 'https://schema.org',
      '@type': 'Service',
      name: 'Mentoria IA para Negócios — Método PPA',
      description: 'Mentoria individual de 90 dias para implementar IA no seu negócio. 12 sessões, diagnóstico completo, autonomia garantida. Para empresários que faturam R$50k+/mês.',
      url: siteUrl('/mentoria'),
      provider: {
        '@type': 'Person',
        name: 'Johnny Vaz',
        url: siteUrl(),
      },
      offers: {
        '@type': 'Offer',
        price: '15000',
        priceCurrency: 'BRL',
      },
      areaServed: 'Brasil',
    };
  }

  if (route === '/about') {
    return {
      '@context': 'https://schema.org',
      '@graph': [
        node(profilePage('/about', 'Sobre — Johnny Vaz')),
        node(person(speaker.bios.medium)),
        node(book()),
      ],
    };
  }

  if (route === '/palestras') {
    return {
      '@context': 'https://schema.org',
      '@graph': [
        node(person(speaker.bios.medium)),
        {
          '@type': 'Service',
          name: 'Palestras, workshops e treinamento in-company',
          description: `${speaker.thesis} Palestras e workshops sobre Inteligência Artificial aplicada e formação de equipes.`,
          url: siteUrl('/palestras'),
          provider: { '@type': 'Person', '@id': PERSON_ID, name: speaker.name, url: siteUrl() },
          areaServed: 'Brasil',
        },
      ],
    };
  }

  if (route === '/imprensa') {
    return { '@context': 'https://schema.org', '@graph': [node(person(speaker.bios.short)), node(book())] };
  }

  return null;
}
